import {Link} from "react-router-dom";
import {ChevronRight} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {Loader} from "@/src/modules/core/components";
import {RESIDENT_PERMANENT_PARKING_RESTRICTION_MESSAGE} from "@/src/modules/home/lib/resident-amenity-restriction-copy";
import {usePermanentParkingPermits} from "@/src/modules/permanent-parking/hooks/use-permanent-parking-permits";

import {ResidentHomePermanentPermitsPreviewRow} from "./resident-home-permanent-permits-preview-row";
import {ResidentAmenityRestrictionNotice} from "./resident-amenity-restriction-notice";

const EXPAND_LIMIT = 10;

type ResidentHomePermanentPermitsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: Record<string, unknown>;
  householdId: number | null;
  isParkingAmenityBlocked?: boolean;
  canShowDetail: boolean;
  onSelectDetail: (id: number) => void;
};

export function ResidentHomePermanentPermitsSheet({
  open,
  onOpenChange,
  filter,
  householdId,
  isParkingAmenityBlocked = false,
  canShowDetail,
  onSelectDetail,
}: ResidentHomePermanentPermitsSheetProps) {
  const {permits, totalRecords, isPending} = usePermanentParkingPermits({
    rows: EXPAND_LIMIT,
    page: 1,
    filters: filter,
    sort: "-end_date",
    enabled: open && Boolean(householdId),
  });

  const handleSelect = (id: number) => {
    if (!canShowDetail) return;
    onSelectDetail(id);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex min-h-0 flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-border flex flex-col gap-2 border-b">
          <SheetTitle>Permanent permits</SheetTitle>
          {isParkingAmenityBlocked ? (
            <ResidentAmenityRestrictionNotice message={RESIDENT_PERMANENT_PARKING_RESTRICTION_MESSAGE} />
          ) : null}
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {!householdId ? (
            <p className="text-muted-foreground text-sm">No household linked.</p>
          ) : isPending ? (
            <Loader className="min-h-48" />
          ) : permits.length === 0 ? (
            <p className="text-muted-foreground text-sm">No permanent permits yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {permits.map((row) => (
                <ResidentHomePermanentPermitsPreviewRow
                  key={row.id}
                  permit={row}
                  amenityRestricted={isParkingAmenityBlocked}
                  onSelect={handleSelect}
                />
              ))}
              {totalRecords > EXPAND_LIMIT ? (
                <p className="text-muted-foreground pt-2 text-center text-xs">
                  Showing {EXPAND_LIMIT} of {totalRecords} — open the full list for more.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <SheetFooter className="border-border border-t px-4 py-3">
          <Button
            render={
              <Link to="/permanent-parking?tab=permits" onClick={() => onOpenChange(false)} />
            }
            variant="outline"
            className="w-full"
          >
            View all on permanent parking page
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
